import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { AuthorSettingsService } from '../../../shared/services/apis/platform/author-settings.service';
import { CouponApplyTargetsRequest } from '../../../shared/services/requests/platform/coupon-apply-targets.request';
import { CouponTargetsResponse } from '../../../shared/services/responses/platform/coupon-targets.response';
import { CouponBookVersionTargetStruct } from '../../../shared/services/structs/coupon-targets.struct';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatOptionModule, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';

export interface CouponApplyModalData {
    idCoupon: number;
}

@Component({
    selector: 'app-coupon-apply-modal',
    standalone: true,
    templateUrl: 'coupon-apply-modal.component.html',
    styleUrl: 'coupon-apply-modal.component.css',
    imports: [CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatButtonModule,
        MatOptionModule,
        MatRadioModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatDialogModule,
        MatButtonModule,
        MatCheckboxModule,
        MatSelectModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CouponApplyModalComponent implements OnInit {
    isLoading = false;
    list: CouponBookVersionTargetStruct[] = [];
    selectAll = true; // por padrão, tudo selecionado

    constructor(
        private dialogRef: MatDialogRef<CouponApplyModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CouponApplyModalData,
        private service: AuthorSettingsService,
        private alert: AlertService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.fetch();
    }

    fetch(): void {
        this.isLoading = true;
        this.service.GetCouponTargets(this.data.idCoupon).subscribe({
            next: (res: CouponTargetsResponse) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alert.show('Erro', res.errorDescription || 'Erro ao carregar itens.', AlertType.error);
                    this.cdr.markForCheck();
                    return;
                }
                // Se o backend vier sem seleção, marcamos tudo como true
                this.list = (res.list || []).map(bv => ({
                    ...bv,
                    kits: (bv.kits || []).map(k => ({ ...k, selected: k.selected ?? true }))
                }));
                this.syncSelectAllFlag();
                this.cdr.markForCheck();
            },
            error: () => {
                this.isLoading = false;
                this.alert.show('Erro', 'Erro inesperado ao carregar itens.', AlertType.error);
                this.cdr.markForCheck();
            }
        });
    }

    // Master checkbox
    toggleSelectAll(evt: MatCheckboxChange): void {
        this.selectAll = !!evt.checked;
        for (const bv of this.list) {
            for (const k of bv.kits) k.selected = this.selectAll;
        }
        this.cdr.markForCheck();
    }

    // Seleciona/desmarca todos os kits de um livro
    toggleAllForBook(bv: CouponBookVersionTargetStruct, checked: boolean): void {
        for (const k of bv.kits) k.selected = checked;
        this.syncSelectAllFlag();
        this.cdr.markForCheck();
    }

    // Quando muda um kit, atualiza os flags agregados
    onKitChange(): void {
        this.syncSelectAllFlag();
        this.cdr.markForCheck();
    }

    private syncSelectAllFlag(): void {
        const all = this.list.flatMap(bv => bv.kits);
        this.selectAll = all.length > 0 && all.every(k => !!k.selected);
    }

    countSelected(): number {
        return this.list.reduce((acc, bv) => acc + bv.kits.filter(k => k.selected).length, 0);
    }

    isAllSelected(bv: CouponBookVersionTargetStruct): boolean {
        const kits = bv?.kits || [];
        return kits.length > 0 && kits.every(k => !!k.selected);
    }

    isPartialSelected(bv: CouponBookVersionTargetStruct): boolean {
        const kits = bv?.kits || [];
        const some = kits.some(k => !!k.selected);
        const all = kits.length > 0 && kits.every(k => !!k.selected);
        return some && !all;
    }

    totalKits(): number {
        return this.list.reduce((acc, bv) => acc + bv.kits.length, 0);
    }

    close(): void {
        this.dialogRef.close();
    }

    save(): void {
        const selectedIds = this.list
            .flatMap(bv => bv.kits)
            .filter(k => k.selected)
            .map(k => k.idKit);

        const req: CouponApplyTargetsRequest = {
            idCoupon: this.data.idCoupon,
            idKitList: selectedIds
        };

        this.isLoading = true;
        this.service.SaveCouponTargets(req).subscribe({
            next: (res: ReturnStruct) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alert.show('Erro', res.errorDescription || 'Erro ao salvar seleção.', AlertType.error);
                    this.cdr.markForCheck();
                    return;
                }
                this.alert.show('Sucesso', 'Aplicação do cupom atualizada!', AlertType.success);
                this.dialogRef.close(true);
            },
            error: () => {
                this.isLoading = false;
                this.alert.show('Erro', 'Erro inesperado ao salvar.', AlertType.error);
                this.cdr.markForCheck();
            }
        });
    }
}
